import useRunOnce from '$/hooks/useRunOnce';
import React from 'react';
import isMobile from './isMobile';

import { screens } from '../../tailwind.config';
import useDebouncedState from '$/hooks/useDebouncedState';

// Hook
interface SizeAndOrientation {
  width: number;
  height: number;
  isLandscape: boolean;
  isPortrait: boolean;
  sm: boolean;
  md: boolean;
  lg: boolean;
  xl: boolean;
}

export const useOnWindowResize = (callback: () => void): void => {
  const hasRunOnce = React.useRef(false);

  React.useEffect(() => {
    window.addEventListener('resize', callback);
    window.addEventListener('orientationchange', callback);

    // Run once in case handleResize doesn't run on initial render
    if (!hasRunOnce.current) {
      hasRunOnce.current = true;
      callback();
    }

    return (): void => {
      window.addEventListener('orientationchange', callback);
      window.removeEventListener('resize', callback);
    };
  }, [callback]);
};

export const useWindowSizeInfo = (): SizeAndOrientation => {
  const getSize = (): SizeAndOrientation => {
    const isSSR = typeof window === 'undefined';

    const isLandscape = !isSSR
      ? window.matchMedia('(orientation: portrait)').matches
        ? false
        : true
      : !isMobile;

    const width = !isSSR ? window.innerWidth : screens.md + 20;
    const height = !isSSR ? window.innerHeight : 768;

    return {
      width,
      height,
      isLandscape,
      isPortrait: !isLandscape,
      sm: width > screens.sm,
      md: width >= screens.md,
      lg: width > screens.lg,
      xl: width >= screens.xl,
    };
  };

  const [windowSize, setWindowSize] = useDebouncedState<SizeAndOrientation>(() => getSize(), 100);

  useRunOnce(() => {
    const handleResize = (): void => {
      const s = getSize();
      setWindowSize(s);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Run once in case handleResize doesn't run on initial render
    handleResize();

    return (): void => {
      window.addEventListener('orientationchange', handleResize);
      window.removeEventListener('resize', handleResize);
    };
  });

  return windowSize;
};
